import { useCallback, useState, type ReactNode } from 'react';
import { BRAND, FONTS } from '../styles/tokens';
import { ScrollProgress, FloatingCTA } from './ScrollProgress';
import { Nav } from './Nav';
import { Footer } from './Sections';
import { LeadCapture } from './LeadCapture';
import { useIsMobile } from '../hooks/useIsMobile';
import type { LeadPrefill } from '../types';

export type OpenLead = (prefill?: LeadPrefill) => void;

interface PageShellProps { children: (openLead: OpenLead) => ReactNode }

/**
 * Layout for the generated static pages (/assessments/<slug>/, /programs/<slug>/).
 * Nav targets that exist on this page scroll; everything else goes back to the home page anchor.
 */
export function PageShell({ children }: PageShellProps) {
  const [leadPrefill, setLeadPrefill] = useState<LeadPrefill | null>(null);

  const nav = useCallback((id: string) => {
    const el = document.getElementById(id);
    if (el) { el.scrollIntoView({ behavior: 'smooth' }); return; }
    window.location.assign(id === 'hero' ? '/' : `/#${id}`);
  }, []);

  const openLead = useCallback<OpenLead>((prefill = {}) => {
    setLeadPrefill({ ...prefill });
    nav('lead');
  }, [nav]);

  return (
    <>
      <a href="#page-content" style={{ position: 'absolute', top: -100, left: 0, background: BRAND.warm, color: BRAND.navy, padding: '12px 24px', zIndex: 999, fontSize: 14, fontFamily: FONTS.body, fontWeight: 700, textDecoration: 'none', borderRadius: '0 0 8px 0' }}
        onFocus={e => e.currentTarget.style.top = '0'}
        onBlur={e => e.currentTarget.style.top = '-100px'}
      >Skip to content</a>
      <ScrollProgress />
      <Nav onNav={nav} />
      <FloatingCTA onNav={nav} />
      <main id="page-content">
        {children(openLead)}
        <LeadCapture prefill={leadPrefill} />
      </main>
      <Footer />
    </>
  );
}

interface PageHeroProps {
  eyebrow: string;
  title: string;
  subtitle?: string;
  icon?: string;
  crumbs: { label: string; href: string }[];
  children?: ReactNode;
}

/** Dark hero band so the transparent fixed Nav is readable; carries breadcrumbs for every subpage. */
export function PageHero({ eyebrow, title, subtitle, icon, crumbs, children }: PageHeroProps) {
  const m = useIsMobile();
  return (
    <section style={{ background: `linear-gradient(160deg, ${BRAND.navy} 0%, ${BRAND.navyLight} 60%, ${BRAND.slate} 100%)`, padding: m ? '96px 20px 48px' : '130px 24px 72px' }}>
      <div style={{ maxWidth: 900, margin: '0 auto' }}>
        <nav aria-label="Breadcrumb" style={{ fontFamily: FONTS.body, fontSize: 12, color: 'rgba(255,255,255,0.45)', marginBottom: 20, display: 'flex', flexWrap: 'wrap', gap: 6 }}>
          {crumbs.map((c, i) => (
            <span key={c.href}>
              <a href={c.href} style={{ color: 'rgba(255,255,255,0.6)', textDecoration: 'none' }}>{c.label}</a>
              {i < crumbs.length - 1 && <span style={{ margin: '0 6px' }}>/</span>}
            </span>
          ))}
          <span style={{ color: BRAND.warm }}>{title}</span>
        </nav>
        <div style={{ fontFamily: FONTS.body, fontSize: 12, letterSpacing: '0.14em', textTransform: 'uppercase', color: BRAND.teal, fontWeight: 600, marginBottom: 14 }}>{eyebrow}</div>
        <h1 style={{ fontFamily: FONTS.heading, fontSize: m ? 32 : 48, fontWeight: 700, color: BRAND.white, lineHeight: 1.1, marginBottom: 14, display: 'flex', gap: 14, alignItems: 'center' }}>
          {icon && <span aria-hidden style={{ fontSize: m ? 30 : 40 }}>{icon}</span>}
          <span>{title}</span>
        </h1>
        {subtitle && <p style={{ fontFamily: FONTS.body, fontSize: m ? 15 : 17, lineHeight: 1.7, color: 'rgba(255,255,255,0.7)', maxWidth: 640 }}>{subtitle}</p>}
        {children}
      </div>
    </section>
  );
}
